import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { headRewrite } from "../function/headRewrite";
import { guideArticles } from "../content/archiveGuide";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      borderLeft: "none",
      borderRight: "none",
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.18,
    letterSpacing: "-0.08em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  subHeading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(22px, 2.8vw, 34px)",
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  heroBody: {
    maxWidth: 670,
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    marginBottom: theme.spacing(4),
    whiteSpace: "pre-line",
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  noteBody: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
    whiteSpace: "pre-line",
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#111",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
      boxShadow: "none",
    },
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  heroVisual: {
    position: "relative",
    minHeight: 540,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 380,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 22,
    width: 270,
    height: 270,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroSquare: {
    position: "absolute",
    left: 24,
    top: 44,
    width: 190,
    height: 190,
    border: "1px solid #111",
    opacity: 0.55,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
      left: 0,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  heroMock: {
    position: "relative",
    width: "74%",
    maxWidth: 420,
    aspectRatio: "1 / 1",
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.18)",
    overflow: "hidden",
    transform: "rotate(2deg)",
    [theme.breakpoints.down("sm")]: {
      width: "84%",
      maxWidth: 320,
    },
  },
  heroMockImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  heroMockLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  heroMockTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(24px, 3vw, 36px)",
    lineHeight: 1.5,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  thesisCard: {
    border: "1px solid #111",
    minHeight: 310,
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  thesisLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  thesisText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(21px, 2.4vw, 30px)",
    lineHeight: 1.72,
    letterSpacing: "-0.03em",
    fontWeight: 500,
  },
  knowledgeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  knowledgeItem: {
    borderLeft: "1px solid #111",
    paddingLeft: theme.spacing(3),
  },
  knowledgeTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.7,
    marginBottom: theme.spacing(1.5),
  },
  brandGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  brandItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.8, 2.2),
    minHeight: 188,
  },
  brandTag: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1.4),
  },
  brandTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  listBlock: {
    borderTop: "1px solid #111",
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "72px 260px 1fr",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  listIndex: {
    fontSize: 14,
    fontWeight: 700,
    color: "#555",
  },
  listTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  card: {
    border: "1px solid #111",
    minHeight: 310,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: 250,
    },
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  quoteBlock: {
    border: "1px solid #111",
    padding: theme.spacing(5),
    position: "relative",
    background: "#111",
    color: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },
  quoteMark: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 72,
    lineHeight: 0.8,
    opacity: 0.75,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(22px, 3vw, 36px)",
    lineHeight: 1.72,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  inventoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  inventoryCard: {
    border: "1px solid #111",
    minHeight: 370,
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  inventoryCardLink: {
    color: "inherit",
    textDecoration: "none",
    transition: "background 180ms ease, transform 180ms ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.36)",
      transform: "translateY(-2px)",
    },
  },
  inventoryImage: {
    aspectRatio: "1 / 1",
    width: "100%",
    border: "1px solid #c8c8c8",
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: 11,
    lineHeight: 1.8,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2),
  },
  inventoryProductImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  inventoryMeta: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1),
  },
  inventoryTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  faqGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  faqItem: {
    border: "1px solid #111",
    padding: theme.spacing(3),
  },
  faqQuestion: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.8,
    marginBottom: theme.spacing(1.5),
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  stickyBrandCta: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 3200,
    padding: theme.spacing(1.4, 2),
    borderTop: "1px solid rgba(17, 17, 17, 0.18)",
    background: "#eeeeee",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  stickyBrandButton: {
    width: "100%",
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.4, 2),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  indexHero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
  },
  indexIntro: {
    maxWidth: 760,
  },
  guideGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  guideCard: {
    border: "1px solid #111",
    minHeight: 320,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    color: "#111",
    textDecoration: "none",
    transition: "background 0.2s ease, color 0.2s ease",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  guideCategory: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  guideCardTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 24,
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    marginBottom: theme.spacing(2),
  },
  guideCardBody: {
    fontSize: 14,
    lineHeight: 2,
  },
  guideArrow: {
    marginTop: theme.spacing(3),
    fontWeight: 700,
    fontSize: 14,
  },
  notFound: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(8, 0),
  },
}));

function trackArchiveGuideEvent(eventName, payload = {}) {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("event", eventName, payload);
}

function Seo({ article, canonicalUrl }) {
  const title = article.seo.title;
  const description = article.seo.description;
  const keyword = article.seo.keyword;
  const ogImage = article.hero.image
    ? `${process.env.REACT_APP_IMAGE_URL || ""}${article.hero.image}`
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "Enclopedia",
          },
          about: [
            "アーカイブファッション",
            "ブランドガイド",
            article.title,
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: typeof window !== "undefined" ? window.location.origin : "",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Archive Guide",
              item: `${typeof window !== "undefined" ? window.location.origin : ""}/archive_guide`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
              item: canonicalUrl,
            },
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: article.sections.faq.items.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        })}
      </script>
    </Helmet>
  );
}

function IndexSeo() {
  const title = "アーカイブガイド｜ブランドを深く知るEnclopedia";
  const description =
    "Enclopediaのアーカイブガイド一覧。ブランドの入口、初めての体験、アーカイブを楽しむための知識をまとめています。";
  const keyword =
    "アーカイブガイド,ブランドガイド,ファッションアーカイブ,コムデギャルソン 初めて,Enclopedia";
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/archive_guide`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: {
            "@type": "WebSite",
            name: "Enclopedia",
            url: typeof window !== "undefined" ? window.location.origin : "",
          },
        })}
      </script>
    </Helmet>
  );
}

function ArchiveGuideIndex({ classes }) {
  return (
    <div className={classes.root}>
      <IndexSeo />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.indexHero}>
            <div className={classes.indexIntro}>
              <div className={classes.labelRow}>
                <span>ARCHIVE GUIDE</span>
                <span className={classes.labelLine} />
              </div>
              <Typography variant="h1" component="h1" className={classes.heroTitle}>
                ブランドを、
                <br />
                深く知る入口。
              </Typography>
              <Typography component="p" className={classes.heroBody}>
                Enclopediaのアーカイブガイドでは、ブランドの背景、初めて着ると見えてくること、アーカイブの楽しみ方を記事として整理します。
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.guideGrid}>
              {guideArticles.map((article) => (
                <a
                  href={`/archive_guide/${article.slug}`}
                  className={classes.guideCard}
                  key={article.slug}
                >
                  <div>
                    <div className={classes.guideCategory}>{article.category}</div>
                    <Typography component="h2" className={classes.guideCardTitle}>
                      {article.title}
                    </Typography>
                    <Typography component="p" className={classes.guideCardBody}>
                      {article.listDescription}
                    </Typography>
                  </div>
                  <div className={classes.guideArrow}>記事を読む →</div>
                </a>
              ))}
            </div>
          </section>
        </div>
      </Container>
    </div>
  );
}

function ArchiveGuideNotFound({ classes }) {
  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <div className={classes.notFound}>
          <div>
            <Typography component="h1" className={classes.heading}>
              記事が見つかりませんでした。
            </Typography>
            <Button className={classes.primaryButton} component="a" href="/archive_guide">
              アーカイブガイド一覧へ戻る
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

function ArchiveGuideDetail({ classes, article }) {
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${article.seo.canonicalPath}`;
  const { hero, thesis, sections } = article;

  return (
    <div className={classes.root}>
      <Seo article={article} canonicalUrl={canonicalUrl} />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>{hero.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  {hero.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < hero.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  {hero.body}
                </Typography>
                <div className={classes.buttonRow}>
                <Button
                  className={classes.primaryButton}
                  component="a"
                  href={hero.ctaHref || "https://www.enclopedia-official.com/"}
                >
                  {hero.ctaLabel || "レンタルできるアイテムを見る"}
                </Button>
                  <a href="#faq" className={classes.textLink}>
                    初めての人が気になりやすいこと
                  </a>
                </div>
                <div className={classes.keywordLine}>
                  {hero.keywords.map((keyword, index) => (
                    <React.Fragment key={keyword}>
                      <span>{keyword}</span>
                      {index < hero.keywords.length - 1 && <span>/</span>}
                    </React.Fragment>
                  ))}
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <div className={classes.heroVisual}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroSquare} />
                  <div className={classes.heroNumber}>{hero.number}</div>
                  <div className={classes.heroMock}>
                    <img
                      src={`${process.env.REACT_APP_IMAGE_URL || ""}${hero.image}`}
                      alt={hero.imageAlt}
                      className={classes.heroMockImage}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>{sections.whyThisPage.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.whyThisPage.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.whyThisPage.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.whyThisPage.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.thesisCard}>
                  <div>
                    <div className={classes.thesisLabel}>KEY THESIS</div>
                    <Typography component="p" className={classes.thesisText}>
                      {thesis.text}
                    </Typography>
                  </div>
                  <div className={classes.keywordLine}>
                    {thesis.keywords.map((keyword, index) => (
                      <React.Fragment key={keyword}>
                        <span>{keyword}</span>
                        {index < thesis.keywords.length - 1 && <span>/</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{sections.brandOverview.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.brandOverview.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.brandOverview.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.brandOverview.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.knowledgeGrid}>
                  {sections.brandOverview.knowledgeItems.map((item) => (
                    <div className={classes.knowledgeItem} key={item.title}>
                      <div className={classes.knowledgeTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{sections.lineGuide.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {sections.lineGuide.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.lineGuide.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <div className={classes.brandGrid}>
              {sections.lineGuide.items.map((item) => (
                <div className={classes.brandItem} key={item.title}>
                  <div className={classes.brandTag}>{item.tag}</div>
                  <Typography component="h3" className={classes.brandTitle}>
                    {item.title}
                  </Typography>
                  <Typography component="p" className={classes.noteBody}>
                    {item.text}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{sections.firstExperience.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.firstExperience.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.firstExperience.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.firstExperience.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.listBlock}>
                  {sections.firstExperience.items.map((item) => (
                    <div className={classes.listItem} key={item.index}>
                      <div className={classes.listIndex}>{item.index}</div>
                      <div className={classes.listTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.quoteBlock}>
              <div className={classes.quoteMark}>“</div>
              <Typography component="p" className={classes.quoteText}>
                {sections.quote.textLines.map((line, index) => (
                  <React.Fragment key={line}>
                    {line}
                    {index < sections.quote.textLines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{sections.whyRent.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {sections.whyRent.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.whyRent.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <Grid container spacing={3}>
              {sections.whyRent.items.map((reason) => (
                <Grid item xs={12} md={4} key={reason.number}>
                  <div className={classes.card}>
                    <div>
                      <div className={classes.cardNumber}>{reason.number}</div>
                      <div className={classes.cardTitle}>{reason.title}</div>
                      <div className={classes.smallRule} />
                      <Typography component="p" className={classes.body}>
                        {reason.text}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.section} id="archive-items">
            <Grid container spacing={6}>
              <Grid item xs={12} md={4}>
                <div className={classes.labelRow}>
                  <span>{sections.inventory.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.inventory.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.inventory.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.inventory.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <div className={classes.inventoryGrid}>
                  {sections.inventory.items.map((item) => (
                    <a
                      className={`${classes.inventoryCard} ${classes.inventoryCardLink}`}
                      href={item.href}
                      key={item.href}
                    >
                      <div>
                        <div className={classes.inventoryImage}>
                          {item.image ? (
                            <img
                              src={`${process.env.REACT_APP_IMAGE_URL || ""}${item.image}`}
                              alt={item.title}
                              className={classes.inventoryProductImage}
                            />
                          ) : (
                            <>
                              Product image
                              <br />
                              coming soon
                            </>
                          )}
                        </div>
                        <div className={classes.inventoryMeta}>{item.meta}</div>
                        <Typography component="h3" className={classes.inventoryTitle}>
                          {item.title}
                        </Typography>
                        <Typography component="p" className={classes.noteBody}>
                          {item.text}
                        </Typography>
                      </div>
                      <div style={{ marginTop: 20 }}>
                        <span className={classes.textLink}>
                          このアイテムを見る
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section} id="faq">
            <div className={classes.labelRow}>
              <span>{sections.faq.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {sections.faq.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.faq.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <div className={classes.faqGrid}>
              {sections.faq.items.map((faq) => (
                <div className={classes.faqItem} key={faq.question}>
                  <div className={classes.faqQuestion}>{faq.question}</div>
                  <Typography component="p" className={classes.noteBody}>
                    {faq.answer}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="h2" className={classes.finalTitle}>
              {sections.finalCta.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.finalCta.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              {sections.finalCta.body}
            </Typography>
            <Button
              className={classes.primaryButton}
              component="a"
              href={sections.finalCta.buttonHref}
            >
              {sections.finalCta.buttonLabel}
            </Button>
          </div>
        </div>
      </Container>
      <div className={classes.stickyBrandCta}>
        <Button
          className={classes.stickyBrandButton}
          component="a"
          href={sections.finalCta.buttonHref}
          onClick={() =>
            trackArchiveGuideEvent("archive_guide_fixed_brand_click", {
              page_type: "detail",
              guide_slug: article.slug,
              position: "sticky_mobile",
              destination: "brand",
            })
          }
        >
          このブランドのアイテムを見る
        </Button>
      </div>
    </div>
  );
}

export default function ArchiveGuideScreen() {
  const classes = useStyles();
  const { slug } = useParams();

  if (!slug) {
    return <ArchiveGuideIndex classes={classes} />;
  }

  const article = guideArticles.find((item) => item.slug === slug);

  if (!article) {
    return <ArchiveGuideNotFound classes={classes} />;
  }

  return <ArchiveGuideDetail classes={classes} article={article} />;
}
